import {
  RouteProp,
  useNavigation,
  useRoute,
  NavigationProp,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { Suspense, useCallback, useEffect } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useQueryLoader, usePreloadedQuery, PreloadedQuery } from "react-relay";

import { Box } from "@whoosh/design-system/src/components/primitive";

import { currentProfileAtom } from "../../features/Club/atoms";
import { ClubStackNavigatorParamList } from "../../navigators/ClubNavigator";
import { HomeTabsNavigatorParamList } from "../../navigators/HomeTabsNavigator/types";
import { useFlags } from "../../utils/launchDarkly";
import { EventSignupSummaryQuery } from "./__generated__/EventSignupSummaryQuery.graphql";
import { EventSignupSummaryContent } from "./components/EventSignupSummaryContent";
import { EventSignupSummarySkeleton } from "./components/EventSignupSummarySkeleton";

export const EventSignupSummaryScreen = () => {
  const route =
    useRoute<RouteProp<ClubStackNavigatorParamList, "EventSignupSummary">>();
  const { eventID, signUpId } = route.params;

  const [queryRef, loadQuery] = useQueryLoader<EventSignupSummaryQuery>(
    EventSignupSummaryQueryString
  );

  const currentProfile = useAtomValue(currentProfileAtom);
  const memberId = currentProfile?.memberId;
  const { patronLoyalty } = useFlags();

  useEffect(() => {
    if (eventID && signUpId) {
      loadQuery(
        { eventID, signUpId, memberId, patronLoyalty: !!patronLoyalty },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [loadQuery, eventID, signUpId, memberId, patronLoyalty]);

  return (
    <EventSignupSummaryLayout>
      <Suspense fallback={<EventSignupSummarySkeleton />}>
        {queryRef ? (
          <EventSignupSummaryQueryRenderer queryRef={queryRef} />
        ) : null}
      </Suspense>
    </EventSignupSummaryLayout>
  );
};

const EventSignupSummaryLayout = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const insets = useSafeAreaInsets();

  return (
    <Box
      width="100%"
      flex={1}
      style={{ paddingTop: insets.top, paddingBottom: insets.bottom }}
      backgroundColor="surface-secondary"
    >
      <Box
        width="100%"
        flex={1}
        backgroundColor="surface-secondary"
        overflow="hidden"
      >
        {children}
      </Box>
    </Box>
  );
};

type EventSignupSummaryQueryRendererProps = {
  queryRef: PreloadedQuery<EventSignupSummaryQuery>;
};

const EventSignupSummaryQueryRenderer = ({
  queryRef,
}: EventSignupSummaryQueryRendererProps) => {
  const data = usePreloadedQuery(EventSignupSummaryQueryString, queryRef);
  return <EventSignupSummaryQueryRendererContent data={data} />;
};

type EventSignupSummaryQueryRendererContentProps = {
  data: EventSignupSummaryQuery["response"];
};

const EventSignupSummaryQueryRendererContent = ({
  data,
}: EventSignupSummaryQueryRendererContentProps) => {
  const navigation =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList>>();

  const handleGoBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate("MainHome");
    }
  }, [navigation]);

  const event = data.event?.__typename === "Event" ? data.event : null;
  const signUp = data.signUp?.__typename === "EventSignUp" ? data.signUp : null;

  if (!event || !signUp) {
    return <EventSignupSummarySkeleton />;
  }

  return (
    <EventSignupSummaryContent
      eventFragmentRef={event}
      signUpFragmentRef={signUp}
      handleGoBack={handleGoBack}
    />
  );
};

const EventSignupSummaryQueryString = graphql`
  query EventSignupSummaryQuery(
    $eventID: ID!
    $signUpId: ID!
    $memberId: ID
    $patronLoyalty: Boolean!
  ) {
    event: node(id: $eventID) {
      __typename
      ... on Event {
        id
        ...EventSignupSummaryContent_event
      }
    }
    signUp: node(id: $signUpId) {
      __typename
      ... on EventSignUp {
        id
        ...EventSignupSummaryContent_signUp
          @arguments(patronLoyalty: $patronLoyalty)
      }
    }
  }
`;
